import { useState, useRef } from 'react';
import type { ChatMessage } from '../components/ChatComponents';
import { threadsApi } from '../api/threads';

type Message = ChatMessage;

interface UseArchiveLoaderParams {
  selectedThreadId: string | null;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setIsPendingResponse: (v: boolean) => void;
  pendingRetryRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
}

export function useArchiveLoader({
  selectedThreadId,
  setMessages,
  setIsPendingResponse,
  pendingRetryRef,
}: UseArchiveLoaderParams) {
  const [archiveOffset, setArchiveOffset] = useState(0);
  const [hasMoreArchive, setHasMoreArchive] = useState(false);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const isLoadingArchiveRef = useRef(false);

  // Helper: sessionStorage cache key for archive messages
  const archiveCacheKey = (threadId: string) => `archive_msgs_${threadId}`;
  const archiveMetaCacheKey = (threadId: string) => `archive_meta_${threadId}`;

  const resetArchive = (threadId?: string) => {
    setArchiveOffset(0);
    setHasMoreArchive(false);
    if (threadId) {
      try {
        sessionStorage.removeItem(archiveCacheKey(threadId));
        sessionStorage.removeItem(archiveMetaCacheKey(threadId));
      } catch {
        /* ignore */
      }
    }
  };

  const clearArchiveCache = (threadId: string) => {
    try {
      sessionStorage.removeItem(archiveCacheKey(threadId));
      sessionStorage.removeItem(archiveMetaCacheKey(threadId));
    } catch {
      /* ignore */
    }
  };

  const loadMoreArchive = async () => {
    if (!selectedThreadId || loadingArchive || !hasMoreArchive) return;
    setLoadingArchive(true);
    isLoadingArchiveRef.current = true;

    // Cancel pending retry to prevent race condition with loadThreadMessages
    if (pendingRetryRef.current) {
      clearTimeout(pendingRetryRef.current);
      pendingRetryRef.current = null;
      setIsPendingResponse(false);
    }

    try {
      const response = await threadsApi.getMessages(selectedThreadId, {
        source: 'archive',
        order: 'asc',
        limit: 10,
        offset: archiveOffset,
      });
      const olderMessages: Message[] = response.messages
        .filter((msg) => ['human', 'user', 'ai', 'assistant'].includes(msg.role) && (msg.content || '').trim())
        .map((msg, index) => ({
          id: msg.id || `archive-load-${archiveOffset}-${index}`,
          role: msg.role === 'human' || msg.role === 'user' ? ('user' as const) : ('assistant' as const),
          content: (msg.content || '').trim(),
          timestamp: new Date(msg.timestamp),
          source: msg.source,
        }));

      const newOffset = archiveOffset + response.messages.length;
      const newHasMore = response.pagination.has_more;

      setMessages((prev) => {
        // Merge and deduplicate by message ID
        const merged = [...olderMessages, ...prev];
        const seen = new Set<string>();
        const deduped = merged.filter((m) => {
          if (seen.has(m.id)) return false;
          seen.add(m.id);
          return true;
        });
        // Sort by timestamp, then UUIDv7 ID as tiebreaker for same-second messages
        deduped.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime() || a.id.localeCompare(b.id));
        // Cache all archive messages to sessionStorage
        const archiveMsgs = deduped.filter((m) => m.source === 'archive');
        try {
          sessionStorage.setItem(archiveCacheKey(selectedThreadId!), JSON.stringify(archiveMsgs));
          sessionStorage.setItem(
            archiveMetaCacheKey(selectedThreadId!),
            JSON.stringify({ offset: newOffset, hasMore: newHasMore }),
          );
        } catch {
          /* quota exceeded, ignore */
        }
        return deduped;
      });
      setArchiveOffset(newOffset);
      setHasMoreArchive(newHasMore);

      // Scroll preservation is handled by VirtualizedMessageList
      requestAnimationFrame(() => {
        isLoadingArchiveRef.current = false;
      });
    } catch (err) {
      console.error('Failed to load archive messages:', err);
      isLoadingArchiveRef.current = false;
    } finally {
      setLoadingArchive(false);
    }
  };

  return {
    archiveOffset,
    setArchiveOffset,
    hasMoreArchive,
    setHasMoreArchive,
    loadingArchive,
    isLoadingArchiveRef,
    loadMoreArchive,
    resetArchive,
    clearArchiveCache,
    archiveCacheKey,
    archiveMetaCacheKey,
  };
}
